import { ImageUploadUrl } from 'types/graphql'

import { useMutation } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import CardSection from 'src/components/Custom/Card/CardSection'
import ColorSelection from 'src/components/Custom/ColorSelection/ColorSelection'
import DragAndDropImageArea from 'src/components/Custom/DragAndDropImageArea/DragAndDropImageArea'
import HeadingLevel from 'src/components/Custom/Heading/Heading'
import TooltipText from 'src/components/Custom/TooltipText/TooltipText'
import {
  CREATE_IMAGE_UPLOAD_URL_MUTATION,
  ImageProfile,
  uploadImageToCloudflare,
} from 'src/lib/images'

import { StationInputType, UrlsType } from '../../../common/types'

export interface BrandingData {
  colorLogoPreviewUrl?: string
  colors: {
    color: string
    id: string
    label?: string
    labelTooltip?: string
  }[]
  faviconPreviewUrl?: string
  images: UrlsType[]
  passportLogoPreviewUrl?: string
  primaryLogoPreviewUrl?: string
  secondaryLogoPreviewUrl?: string
  whiteLogoPreviewUrl?: string
}

interface BrandingProps {
  data: BrandingData
  onChange: (data: {
    colors?: { label?: string; color: string; id: string }[]
    images?: UrlsType[]
  }) => void
  updateStation: (inputData: StationInputType) => Promise<void>
}

const Branding = ({ data, onChange, updateStation }: BrandingProps) => {
  const [createImageUploadUrl] = useMutation<{
    createImageUploadUrl: ImageUploadUrl
  }>(CREATE_IMAGE_UPLOAD_URL_MUTATION)

  const handleImageUpload = async (files: File[], profileToUpdate: string) => {
    const updatedImages = [...data.images]

    const uploadImage = async () => {
      const mutationResult = await createImageUploadUrl()
      const { uploadUrl } = mutationResult.data.createImageUploadUrl
      const newImageUrl = await uploadImageToCloudflare(uploadUrl, files[0])
      const imageIndex = updatedImages.findIndex(
        (image) => image.profile === profileToUpdate
      )

      if (imageIndex !== -1) {
        updatedImages[imageIndex] = {
          profile: profileToUpdate,
          url: newImageUrl,
        }
      } else {
        updatedImages.push({ profile: profileToUpdate, url: newImageUrl })
      }

      updateStation({ images: updatedImages }).then(() => {
        onChange({ images: updatedImages })
      })
    }

    await toast.promise(uploadImage(), {
      loading: 'Uploading logo...',
      success: 'Logo uploaded successfully',
      error: 'Error uploading logo',
    })
  }

  return (
    <div className="w-full">
      <div className="flex flex-col justify-center">
        <ColorSelection
          colors={data.colors}
          onColorChange={(colors) => {
            onChange({ colors: colors })

            const colorPrimary = colors.find((item) => item.id === 'primary')
            const colorSecondary = colors.find(
              (item) => item.id === 'secondary'
            )
            const colorBackground = colors.find(
              (item) => item.id === 'background'
            )
            updateStation({
              colorPrimary: colorPrimary.color,
              colorSecondary: colorSecondary.color,
              colorBackground: colorBackground.color,
            })
          }}
        />

        <CardSection
          title={
            <HeadingLevel level={2} levelStyle={4}>
              Logos
            </HeadingLevel>
          }
        >
          <div className="flex flex-col">
            <div className="mb-2 flex flex-col">
              <TooltipText
                text={
                  <HeadingLevel level={3} levelStyle={5}>
                    Primary
                  </HeadingLevel>
                }
              >
                The primary logo is used on user-facing device activation pages.
                It is the colored/dark version of your logo to be visible on a
                white background.
              </TooltipText>
              <div className="flex flex-col lg:flex-row">
                <div className="mb-2 w-full lg:w-1/2">
                  <div className="mt-6">
                    {data?.primaryLogoPreviewUrl ? (
                      <img
                        className="max-w-full object-contain"
                        src={data.primaryLogoPreviewUrl}
                        alt="Primary logo"
                      />
                    ) : (
                      <em>Not set</em>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <DragAndDropImageArea
                    id="primaryLogo"
                    onUpload={(files) => {
                      handleImageUpload(
                        files as File[],
                        ImageProfile.STATION_LOGO_PRIMARY
                      )
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="mb-2 flex flex-col">
              <TooltipText
                text={
                  <HeadingLevel level={3} levelStyle={5}>
                    Secondary
                  </HeadingLevel>
                }
              >
                The secondary logo is a stacked variation of your logo (or other
                orientation). It is optional and can be used to optimize the
                channel icon. A white logo is preferred.
              </TooltipText>
              <div className="flex flex-col lg:flex-row">
                <div className="mb-2 w-full lg:w-1/2">
                  <div className="mt-6">
                    {data?.secondaryLogoPreviewUrl ? (
                      <img
                        className="max-w-full object-contain"
                        src={data.secondaryLogoPreviewUrl}
                        alt="secondary logo"
                      />
                    ) : (
                      <em>Not set</em>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <DragAndDropImageArea
                    id="secondaryLogo"
                    onUpload={(files) =>
                      handleImageUpload(
                        files as File[],
                        ImageProfile.STATION_LOGO_SECONDARY
                      )
                    }
                  />
                </div>
              </div>
            </div>{' '}
            <div className="mb-2 flex flex-col">
              <TooltipText
                text={
                  <HeadingLevel level={3} levelStyle={5}>
                    Passport
                  </HeadingLevel>
                }
              >
                The Passport logo is used where Passport benefits are displayed
                or to indicate Passport content.
              </TooltipText>
              <div className="flex flex-col lg:flex-row">
                <div className="mb-2 w-full lg:w-1/2">
                  <div className="mt-6">
                    {data?.passportLogoPreviewUrl ? (
                      <img
                        className="max-w-full object-contain"
                        src={data.passportLogoPreviewUrl}
                        alt="Passport logo"
                      />
                    ) : (
                      <em>Not set</em>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <DragAndDropImageArea
                    id="passportLogo"
                    onUpload={(files) =>
                      handleImageUpload(
                        files as File[],
                        ImageProfile.STATION_PASSPORT_LOGO
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="mb-2 flex flex-col">
              <TooltipText
                text={
                  <HeadingLevel level={3} levelStyle={5}>
                    Color Logo
                  </HeadingLevel>
                }
              >
                The color logo is currently only used in the admin sidebar.
              </TooltipText>
              <div className="flex flex-col lg:flex-row">
                <div className="mb-2 w-full lg:w-1/2">
                  <div className="mt-6">
                    {data?.colorLogoPreviewUrl ? (
                      <img
                        className="max-w-full object-contain"
                        src={data.colorLogoPreviewUrl}
                        alt="color logo"
                      />
                    ) : (
                      <em>Not set</em>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <DragAndDropImageArea
                    id="colorLogo"
                    onUpload={(files) =>
                      handleImageUpload(
                        files as File[],
                        ImageProfile.STATION_COLOR_LOGO
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="mb-2 flex flex-col">
              <TooltipText
                text={
                  <HeadingLevel level={3} levelStyle={5}>
                    White Logo
                  </HeadingLevel>
                }
              >
                <p className="mb-1 font-medium text-red-600">
                  White logo aspect ratio must be 5.29 (recommended size:
                  720x136px).
                </p>
                <p>
                  The white logo is the most seen version of your logo, used on
                  the loading screen, home screen, and menu of the app. It can
                  also be used as the channel icon. It must be white to contrast
                  against the background color.
                </p>
              </TooltipText>
              <div className="flex flex-col lg:flex-row">
                <div className="mb-2 w-full lg:w-1/2">
                  <div className="mt-6">
                    {data?.whiteLogoPreviewUrl ? (
                      <img
                        className="max-w-full object-contain"
                        src={data.whiteLogoPreviewUrl}
                        alt="white logo"
                      />
                    ) : (
                      <em>Not set</em>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <DragAndDropImageArea
                    previewClassName="h-[68px] w-[360px] bg-black object-fill mx-auto"
                    id="whiteLogo"
                    onUpload={(files) =>
                      handleImageUpload(
                        files as File[],
                        ImageProfile.STATION_WHITE_LOGO
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="mb-2 flex flex-col">
              <TooltipText
                text={
                  <HeadingLevel level={3} levelStyle={5}>
                    Favicon
                  </HeadingLevel>
                }
              >
                <p className="mb-1 font-medium text-red-600">
                  Favicon aspect ratio must be 1:1 (recommended size:
                  1024x1024).
                </p>
                <p>The favicon is used for the station web portal.</p>
              </TooltipText>
              <div className="flex flex-col lg:flex-row">
                <div className="mb-2 w-full lg:w-1/2">
                  <div className="mt-6">
                    {data?.faviconPreviewUrl ? (
                      <img
                        className="max-w-full object-contain"
                        src={data.faviconPreviewUrl}
                        alt="station favicon"
                      />
                    ) : (
                      <em>Not set</em>
                    )}
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <DragAndDropImageArea
                    previewClassName="w-64 bg-black object-fill mx-auto"
                    id="favicon"
                    onUpload={(files) =>
                      handleImageUpload(
                        files as File[],
                        ImageProfile.STATION_FAVICON
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </CardSection>
      </div>
    </div>
  )
}

export default Branding
